import React from 'react';
import { toast } from 'react-toastify';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { ArcButton, ArcStack, ArcText } from '@src/core/ui';
import { useConfirm } from '@src/core/controllers';
import { ACCESS_TOKEN_KEY } from '@src/core/config';
import { useDeleteMyAccountMutation } from '@src/features/profile/redux';
import { useGetUserDataQuery, UserEntity, UserRoleType } from '@src/core/features';
import { trackError, toTrackedError } from '@src/core/sentry';
import { DeleteAccountButtonProps } from './types';

const OWNER_DELETE_ERROR =
  'You need to transfer ownership to another user before proceeding.';

const DeleteAccountButton: React.FC<DeleteAccountButtonProps> = () => {
  const { confirm } = useConfirm();
  const { data = {} as UserEntity } = useGetUserDataQuery();
  const [deleteMyAccount, { isLoading }] = useDeleteMyAccountMutation();

  const userId = data?.id;
  const isOwner = data?.role === UserRoleType.OWNER;

  const onDelete = async () => {
    if (!userId) return;

    if (isOwner) {
      toast.error(OWNER_DELETE_ERROR);
      return;
    }

    const confirmed = await confirm(
      'This will permanently delete your account and all associated data. This action cannot be undone.',
      'Delete Account?'
    );
    if (!confirmed) return;

    deleteMyAccount(userId).unwrap()
      .then(() => {
        localStorage.removeItem(ACCESS_TOKEN_KEY);
        window.location.reload();
      })
      .catch((err: unknown) => {
        trackError(toTrackedError(err), { code: 'FE_0019', extra: { action: 'deleteAccount' } });
        const message =
          (err as { data?: { message?: string }; message?: string })?.data?.message ||
          (err as { message?: string })?.message ||
          'Failed to delete account';
        toast.error(message);
      });
  };

  return (
    <ArcStack direction='row'>
      <ArcButton
        variant='contained'
        color='error'
        startIcon={<DeleteOutlineIcon />}
        disabled={!userId}
        isLoading={isLoading}
        onClick={onDelete}>
        <ArcText.button color='error.contrastText'>
          Delete Account
        </ArcText.button>
      </ArcButton>
    </ArcStack>
  );
};

export default DeleteAccountButton;
